import React, { useEffect, useMemo, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Activity, ChevronDown, Lock, Unlock, Info, RotateCcw, X, Search, Check, Thermometer, Wind, Compass, CloudRain, Sun, Gauge, Cloud, Zap, Euro, TrendingUp, BarChart3, Droplets, ArrowLeftRight } from 'lucide-react';
export interface VariableOverride {
  variable: string;
}
const VARIABLES = [
// JUA Weather Variables
'Temp 2m', 'Dew Point 2m', 'Wind Speed 10m', 'Wind Dir 10m', 'Wind Speed 100m', 'Wind Dir 100m', 'Precipitation 1h', 'SSRD 1h', 'MSL Pressure', 'Cloud Cover High', 'Cloud Cover Medium', 'Cloud Cover Low',
// ENTSOE Energy Market Variables
'Day-ahead Prices', 'Imbalance Prices', 'Generation (Actual)', 'Generation Forecast (Day-ahead)', 'Wind & Solar Forecast (Day-ahead)', 'Wind & Solar Forecast (Intraday)', 'Load (Actual)', 'Load Forecast (Day-ahead)', 'Available Hydro Capacity', 'Cross-border Flows', 'NTC (Day-ahead)', 'NTC Weekahead', 'NTC Monthahead', 'NTC Yearahead', 'Imbalance Volumes', 'Net Position (Day-ahead)', 'Net Position (Total)', 'Scheduled Exchanges (Day-ahead)', 'Scheduled Exchanges (Total)'];
// --- Icon tile data ---
interface VarTile {
  variable: string;
  label: string;
  icon: React.ElementType;
  badge?: string;
}
interface VarCategory {
  name: string;
  tiles: VarTile[];
}
interface VarSection {
  section: string;
  categories: VarCategory[];
}
const SECTIONS: VarSection[] = [{
  section: 'Weather',
  categories: [{
    name: 'Temperature & Atmosphere',
    tiles: [{
      variable: 'Temp 2m',
      label: 'Temp 2m',
      icon: Thermometer
    }, {
      variable: 'Dew Point 2m',
      label: 'Dew Pt 2m',
      icon: Thermometer,
      badge: 'D'
    }, {
      variable: 'Precipitation 1h',
      label: 'Precip 1h',
      icon: CloudRain
    }, {
      variable: 'SSRD 1h',
      label: 'SSRD 1h',
      icon: Sun,
      badge: '1h'
    }, {
      variable: 'MSL Pressure',
      label: 'MSL Pres',
      icon: Gauge
    }]
  }, {
    name: 'Wind',
    tiles: [{
      variable: 'Wind Speed 10m',
      label: 'Speed 10m',
      icon: Wind,
      badge: '10'
    }, {
      variable: 'Wind Dir 10m',
      label: 'Dir 10m',
      icon: Compass,
      badge: '10'
    }, {
      variable: 'Wind Speed 100m',
      label: 'Speed 100m',
      icon: Wind,
      badge: '100'
    }, {
      variable: 'Wind Dir 100m',
      label: 'Dir 100m',
      icon: Compass,
      badge: '100'
    }]
  }, {
    name: 'Cloud Cover',
    tiles: [{
      variable: 'Cloud Cover High',
      label: 'High',
      icon: Cloud,
      badge: 'H'
    }, {
      variable: 'Cloud Cover Medium',
      label: 'Medium',
      icon: Cloud,
      badge: 'M'
    }, {
      variable: 'Cloud Cover Low',
      label: 'Low',
      icon: Cloud,
      badge: 'L'
    }]
  }]
}, {
  section: 'Energy & Market',
  categories: [{
    name: 'Power & Generation',
    tiles: [{
      variable: 'Generation (Actual)',
      label: 'Gen Act',
      icon: Zap
    }, {
      variable: 'Generation Forecast (Day-ahead)',
      label: 'Gen FC DA',
      icon: Zap
    }]
  }, {
    name: 'Market & Pricing',
    tiles: [{
      variable: 'Day-ahead Prices',
      label: 'DA Prices',
      icon: Euro
    }, {
      variable: 'Imbalance Prices',
      label: 'Imb Prices',
      icon: TrendingUp
    }, {
      variable: 'Imbalance Volumes',
      label: 'Imb Volumes',
      icon: BarChart3
    }]
  }, {
    name: 'Load',
    tiles: [{
      variable: 'Load (Actual)',
      label: 'Load Act',
      icon: Activity
    }, {
      variable: 'Load Forecast (Day-ahead)',
      label: 'Load FC DA',
      icon: BarChart3
    }]
  }, {
    name: 'Renewables & Hydro',
    tiles: [{
      variable: 'Wind & Solar Forecast (Day-ahead)',
      label: 'W&S FC DA',
      icon: Sun
    }, {
      variable: 'Wind & Solar Forecast (Intraday)',
      label: 'W&S FC ID',
      icon: Sun
    }, {
      variable: 'Available Hydro Capacity',
      label: 'Hydro Cap',
      icon: Droplets
    }]
  }, {
    name: 'Transmission',
    tiles: [{
      variable: 'Cross-border Flows',
      label: 'XB Flows',
      icon: ArrowLeftRight
    }, {
      variable: 'NTC (Day-ahead)',
      label: 'NTC DA',
      icon: ArrowLeftRight
    }, {
      variable: 'NTC Weekahead',
      label: 'NTC Wk',
      icon: ArrowLeftRight
    }, {
      variable: 'NTC Monthahead',
      label: 'NTC Mo',
      icon: ArrowLeftRight
    }, {
      variable: 'NTC Yearahead',
      label: 'NTC Yr',
      icon: ArrowLeftRight
    }, {
      variable: 'Net Position (Day-ahead)',
      label: 'Net Pos DA',
      icon: ArrowLeftRight
    }, {
      variable: 'Net Position (Total)',
      label: 'Net Pos Tot',
      icon: ArrowLeftRight
    }, {
      variable: 'Scheduled Exchanges (Day-ahead)',
      label: 'Sched DA',
      icon: ArrowLeftRight
    }, {
      variable: 'Scheduled Exchanges (Total)',
      label: 'Sched Tot',
      icon: ArrowLeftRight
    }]
  }]
}];
// Flatten for quick lookup
const ALL_TILES = SECTIONS.flatMap(s => s.categories.flatMap(c => c.tiles));
function IconTile({
  tile,
  isSelected,
  onClick
}: {
  tile: VarTile;
  isSelected: boolean;
  onClick: () => void;
}) {
  const Icon = tile.icon;
  return <button onClick={onClick} title={tile.variable} className={`group relative flex flex-col items-center justify-center gap-1 rounded-lg py-2 px-1 transition-all ${isSelected ? 'bg-[#EC4899]/10 border border-[#EC4899]/40 shadow-[0_0_12px_rgba(236,72,153,0.08)]' : 'border border-transparent hover:bg-white/[0.04] hover:border-white/[0.06]'}`} data-id="element-2235">
      <div className="relative" data-id="element-2236">
        <Icon size={22} strokeWidth={1.6} className={`transition-colors ${isSelected ? 'text-[#EC4899]' : 'text-slate-400 group-hover:text-slate-200'}`} data-id="element-2237" />
        {tile.badge && <span className={`absolute -top-1.5 -right-2.5 min-w-[14px] h-[14px] flex items-center justify-center rounded-full text-[7px] font-bold leading-none px-0.5 ${isSelected ? 'bg-[#EC4899] text-white' : 'bg-slate-600 text-slate-200 group-hover:bg-slate-500'}`} data-id="element-2238">
            {tile.badge}
          </span>}
      </div>
      <span className={`text-[10px] leading-tight text-center font-medium truncate w-full ${isSelected ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'}`} data-id="element-2239">
        {tile.label}
      </span>
      {isSelected && <div className="absolute top-1 right-1" data-id="element-2240">
          <Check size={9} className="text-[#EC4899]" data-id="element-2241" />
        </div>}
    </button>;
}
interface VariableSelectorProps {
  onOverrideChange?: (override: VariableOverride | null) => void;
  mode?: 'override' | 'page';
  defaultVariable?: string;
}
export function VariableSelector({
  onOverrideChange,
  mode = 'override',
  defaultVariable
}: VariableSelectorProps) {
  const isPageMode = mode === 'page';
  const [open, setOpen] = useState(false);
  const [overrideActive, setOverrideActive] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState<string | null>(defaultVariable || null);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(target) && triggerRef.current && !triggerRef.current.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  const filteredVariables = useMemo(() => {
    if (!search) return VARIABLES;
    const lower = search.toLowerCase();
    return VARIABLES.filter(v => v.toLowerCase().includes(lower));
  }, [search]);
  const toggleOverride = () => {
    const next = !overrideActive;
    setOverrideActive(next);
    if (next && selectedVariable) {
      onOverrideChange?.({
        variable: selectedVariable
      });
    } else {
      onOverrideChange?.(null);
    }
  };
  useEffect(() => {
    if (isPageMode) {
      if (selectedVariable) {
        onOverrideChange?.({
          variable: selectedVariable
        });
      } else {
        onOverrideChange?.(null);
      }
    } else {
      if (overrideActive && selectedVariable) {
        onOverrideChange?.({
          variable: selectedVariable
        });
      } else if (overrideActive && !selectedVariable) {
        setOverrideActive(false);
        onOverrideChange?.(null);
      }
    }
  }, [selectedVariable, overrideActive, onOverrideChange, isPageMode]);
  const resetSelection = () => {
    setSelectedVariable(null);
    setSearch('');
    if (overrideActive) {
      setOverrideActive(false);
      onOverrideChange?.(null);
    }
  };
  const getPanelPosition = () => {
    if (!triggerRef.current) return {
      top: 0,
      left: 0
    };
    const rect = triggerRef.current.getBoundingClientRect();
    const panelWidth = 560;
    const left = Math.min(rect.left, window.innerWidth - panelWidth - 16);
    return {
      top: rect.bottom + 8,
      left: Math.max(8, left)
    };
  };
  const dropdownStyle: React.CSSProperties = {
    background: 'rgba(11, 17, 32, 0.97)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  const isSearching = search.length > 0;
  return <>
      {/* Trigger Button — unchanged */}
      <button ref={triggerRef} onClick={() => setOpen(!open)} className={`jua-toolbar-btn flex items-center gap-2 h-8 px-3 rounded-lg text-xs font-medium border transition-all ${!isPageMode && overrideActive ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' : open ? 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : 'bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-white/[0.08] hover:text-white'}`} data-id="element-2242">
        {!isPageMode && overrideActive ? <Lock size={12} className="text-amber-400" data-id="element-2243" /> : <Activity size={12} className={open ? 'text-[#EC4899]' : 'text-slate-500'} data-id="element-2244" />}
        <span className="font-bold max-w-[100px] truncate" data-id="element-2245">
          {selectedVariable ? selectedVariable : isPageMode ? 'Select Variable' : 'Variable'}
        </span>
        {!isPageMode && overrideActive && <>
            <span className="text-amber-500/40" data-id="element-2246">·</span>
            <span className="text-amber-400/70 text-[10px]" data-id="element-2247">Override</span>
          </>}
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''} ${!isPageMode && overrideActive ? 'text-amber-500' : open ? 'text-[#EC4899]' : 'text-slate-500'}`} data-id="element-2248" />
      </button>

      {/* Dropdown Panel */}
      {open && ReactDOM.createPortal(<div ref={panelRef} className="jua-variable-panel fixed rounded-xl shadow-2xl shadow-black/60 border border-white/[0.08] overflow-hidden z-[9999] flex flex-col" style={{
      ...dropdownStyle,
      top: getPanelPosition().top,
      left: getPanelPosition().left,
      width: isSearching ? 320 : 560,
      maxHeight: 580,
      transition: 'width 0.2s ease'
    }} data-id="element-2249">
            {/* Inner glow */}
            <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 50%, rgba(255,255,255,0.01) 100%)'
      }} data-id="element-2250" />

            {/* Header */}
            <div className="relative flex items-center justify-between px-4 py-3 border-b border-white/[0.06]" data-id="element-2251">
              <div className="flex items-center gap-2" data-id="element-2252">
                <h3 className="text-sm font-semibold text-white" data-id="element-2253">
                  {isPageMode ? 'Select Variable' : 'Variable Override'}
                </h3>
                <span className="text-[10px] text-slate-500 font-medium" data-id="element-2254">
                  {VARIABLES.length} available
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2255">
                <button onClick={resetSelection} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors" data-id="element-2256">
                  <RotateCcw size={11} data-id="element-2257" /> Reset
                </button>
                <button onClick={() => setOpen(false)} className="p-1 rounded-md text-slate-500 hover:text-white hover:bg-white/[0.06] transition-colors" data-id="element-2258">
                  <X size={14} data-id="element-2259" />
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="relative p-3 border-b border-white/[0.06]" data-id="element-2260">
              <div className="relative" data-id="element-2261">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" data-id="element-2262" />
                <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search variables..." className="w-full h-8 pl-8 pr-3 rounded-lg bg-black/20 border border-white/[0.08] text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-white/20 transition-all" data-id="element-2263" />
              </div>
            </div>

            {/* Content Area */}
            <div className="relative flex-1 overflow-y-auto bg-white/[0.01]" data-id="element-2264">
              {isSearching /* ── Flat list fallback when searching ── */ ? <div className="px-2 py-2 space-y-0.5" data-id="element-2265">
                  {filteredVariables.length === 0 ? <div className="py-8 text-center text-xs text-white/30" data-id="element-2266">
                      No variables found
                    </div> : filteredVariables.map(v => {
            const tile = ALL_TILES.find(t => t.variable === v);
            const Icon = tile?.icon || Activity;
            return <button key={v} onClick={() => {
              setSelectedVariable(v);
              setSearch('');
            }} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all ${selectedVariable === v ? 'bg-[#EC4899]/10 text-white' : 'text-slate-300 hover:bg-white/[0.06] hover:text-white'}`} data-id="element-2267">
                          <Icon size={14} className={selectedVariable === v ? 'text-[#EC4899]' : 'text-slate-500'} data-id="element-2268" />
                          <span className="text-xs font-medium flex-1" data-id="element-2269">
                            {v}
                          </span>
                          {selectedVariable === v && <Check size={12} className="text-[#EC4899]" data-id="element-2270" />}
                        </button>;
          })}
                </div> /* ── Icon grid view ── */ : <div className="p-2.5 space-y-3" data-id="element-2271">
                  {SECTIONS.map(section => <div key={section.section} data-id="element-2272">
                      {/* Section header */}
                      <div className="flex items-center gap-2 mb-2" data-id="element-2273">
                        <span className="text-[9px] font-bold text-white/25 uppercase tracking-[0.15em]" data-id="element-2274">
                          {section.section}
                        </span>
                        <div className="flex-1 h-px bg-white/[0.04]" data-id="element-2275" />
                      </div>

                      {/* Categories within section */}
                      <div className="space-y-2.5" data-id="element-2276">
                        {section.categories.map(cat => <div key={cat.name} data-id="element-2277">
                            <span className="text-[9px] font-semibold text-white/40 uppercase tracking-widest mb-1 block pl-0.5" data-id="element-2278">
                              {cat.name}
                            </span>
                            <div className="grid grid-cols-4 gap-1" data-id="element-2279">
                              {cat.tiles.map(tile => <IconTile key={tile.variable} tile={tile} isSelected={selectedVariable === tile.variable} onClick={() => setSelectedVariable(tile.variable)} data-id="element-2280" />)}
                            </div>
                          </div>)}
                      </div>
                    </div>)}
                </div>}
            </div>

            {/* Footer */}
            <div className="relative px-4 py-2.5 flex items-center justify-between text-[11px] border-t border-white/[0.06]" data-id="element-2281">
              <div className="flex items-center gap-1.5" data-id="element-2282">
                <span className="text-slate-400" data-id="element-2283">Selected:</span>
                <span className={`font-bold truncate max-w-[180px] ${selectedVariable ? 'text-white' : 'text-slate-500'}`} data-id="element-2284">
                  {selectedVariable || 'None'}
                </span>
              </div>

              {!isPageMode && <button onClick={toggleOverride} disabled={!selectedVariable} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border ${overrideActive ? 'bg-amber-500/15 border-amber-500/30 text-amber-400 hover:bg-amber-500/25' : !selectedVariable ? 'bg-white/[0.02] border-white/[0.04] text-slate-600 cursor-not-allowed' : 'bg-white/[0.04] border-white/[0.08] text-slate-400 hover:text-white hover:bg-white/[0.08]'}`} data-id="element-2285">
                  {overrideActive ? <Lock size={10} data-id="element-2286" /> : <Unlock size={10} data-id="element-2287" />}
                  {overrideActive ? 'Override Active' : 'Override All'}
                </button>}
            </div>
          </div>, document.body)}
    </>;
}